<template>
  <div v-if="visible" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="fixed inset-0 bg-black/50" @click="handleClose"></div>
    <div class="relative min-h-screen flex items-center justify-center p-4">
      <div class="relative bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h3 class="text-lg font-semibold text-slate-900">订单详情</h3>
            <p class="text-sm text-slate-500">{{ orderDetail?.orderNo }}</p>
          </div>
          <button @click="handleClose" class="p-1.5 text-slate-400 hover:text-slate-600">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div class="overflow-y-auto max-h-[calc(90vh-140px)]">
          <!-- 状态标签 -->
          <div class="px-6 py-3 bg-slate-50 border-b border-slate-200 flex items-center gap-3">
            <span :class="['status-badge text-sm', getStatusClass(orderDetail?.status)]">
              {{ getStatusText(orderDetail?.status) }}
            </span>
            <button
              v-if="hasPendingApproval(orderDetail)"
              type="button"
              class="table-action-button is-purple"
              title="查看审批详情" aria-label="查看审批详情"
              @click="emit('approval-detail', orderDetail)"
            >
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
              </svg>
            </button>
          </div>

          <div class="p-6">
            <!-- 基本信息 -->
            <div class="mb-6">
              <h4 class="text-sm font-semibold text-slate-800 mb-3">订单信息</h4>
              <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div class="bg-slate-50 rounded-xl p-3.5 border border-slate-100 hover:border-slate-200 transition-colors">
                  <div class="text-xs text-slate-400 mb-1.5">产品名称</div>
                  <div class="text-sm font-medium text-slate-900 truncate">{{ orderDetail?.productNameSnapshot || '-' }}</div>
                </div>
                <div class="bg-slate-50 rounded-xl p-3.5 border border-slate-100 hover:border-slate-200 transition-colors">
                  <div class="text-xs text-slate-400 mb-1.5">产品编码</div>
                  <div class="text-sm font-medium text-slate-900 truncate">{{ orderDetail?.productCodeSnapshot || '-' }}</div>
                </div>
                <div class="bg-slate-50 rounded-xl p-3.5 border border-slate-100 hover:border-slate-200 transition-colors">
                  <div class="text-xs text-slate-400 mb-1.5">订单金额</div>
                  <div class="text-sm font-semibold text-primary-600">¥{{ orderDetail?.amount?.toLocaleString() || '0' }}</div>
                </div>
                <div class="bg-slate-50 rounded-xl p-3.5 border border-slate-100 hover:border-slate-200 transition-colors">
                  <div class="text-xs text-slate-400 mb-1.5">创建时间</div>
                  <div class="text-sm text-slate-900">{{ $formatDateTime(orderDetail?.createTime) }}</div>
                </div>
                <div class="bg-slate-50 rounded-xl p-3.5 border border-slate-100 hover:border-slate-200 transition-colors">
                  <div class="text-xs text-slate-400 mb-1.5">服务开始</div>
                  <div class="text-sm text-slate-900">{{ orderDetail?.serviceStart || '-' }}</div>
                </div>
                <div class="bg-slate-50 rounded-xl p-3.5 border border-slate-100 hover:border-slate-200 transition-colors">
                  <div class="text-xs text-slate-400 mb-1.5">服务结束</div>
                  <div class="text-sm text-slate-900">{{ orderDetail?.serviceEnd || '-' }}</div>
                </div>
                <div class="bg-slate-50 rounded-xl p-3.5 border border-slate-100 hover:border-slate-200 transition-colors md:col-span-2">
                  <div class="text-xs text-slate-400 mb-1.5">备注</div>
                  <div class="text-sm text-slate-600">{{ orderDetail?.remark || '-' }}</div>
                </div>
              </div>
            </div>

            <!-- 人员信息 -->
            <div class="mb-6">
              <h4 class="text-sm font-semibold text-slate-800 mb-3">相关人员</h4>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <!-- 客户信息 -->
                <div class="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-xl p-4">
                  <div class="flex items-center gap-2 mb-3">
                    <div class="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                      <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                      </svg>
                    </div>
                    <span class="text-xs font-medium text-blue-600">客户信息</span>
                  </div>
                  <div v-if="orderDetail?.customer" class="space-y-2">
                    <div class="flex items-center gap-2">
                      <span class="font-semibold text-slate-900">{{ orderDetail.customer.name }}</span>
                      <span class="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">{{ orderDetail.customer.statusName || '未知' }}</span>
                    </div>
                    <!-- <div class="text-xs text-slate-500">{{ orderDetail.customer.customerNo }}</div> -->
                    <div class="flex items-center gap-1.5 text-sm text-slate-600">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                      </svg>
                      <span>{{ orderDetail.customer.phone || '-' }}</span>
                    </div>
                    <div v-if="orderDetail.customer.address" class="flex items-start gap-1.5 text-xs text-slate-500">
                      <svg class="w-3.5 h-3.5 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                      </svg>
                      <span>{{ orderDetail.customer.address }}{{ orderDetail.customer.detailAddress ? ' ' + orderDetail.customer.detailAddress : '' }}</span>
                    </div>
                  </div>
                  <div v-else class="text-sm text-slate-400">暂无客户信息</div>
                </div>

                <!-- 销售人员信息 -->
                <div class="bg-gradient-to-br from-green-50 to-white border border-green-100 rounded-xl p-4">
                  <div class="flex items-center gap-2 mb-3">
                    <div class="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                      <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                      </svg>
                    </div>
                    <span class="text-xs font-medium text-green-600">销售人员</span>
                  </div>
                  <div v-if="orderDetail?.createUser" class="space-y-2">
                    <div class="flex items-center gap-2">
                      <img
                        v-if="orderDetail.createUser.avatarUrl"
                        :src="orderDetail.createUser.avatarUrl"
                        class="w-8 h-8 rounded-full object-cover"
                        alt=""
                      />
                      <div v-else class="w-8 h-8 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                        {{ orderDetail.createUser.displayName?.charAt(0) }}
                      </div>
                      <span class="font-semibold text-slate-900">{{ orderDetail.createUser.displayName }}</span>
                    </div>
                    <!-- <div v-if="orderDetail.createUser.roleNames?.length" class="flex flex-wrap gap-1">
                      <span v-for="role in orderDetail.createUser.roleNames" :key="role" class="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded">{{ role }}</span>
                    </div> -->
                    <div class="flex items-center gap-1.5 text-sm text-slate-600">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                      </svg>
                      <span>{{ orderDetail.createUser.phone || '-' }}</span>
                    </div>
                    <div v-if="orderDetail.createUser.email" class="flex items-center gap-1.5 text-xs text-slate-500">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                      </svg>
                      <span>{{ orderDetail.createUser.email }}</span>
                    </div>
                  </div>
                  <div v-else class="text-sm text-slate-400">暂无销售人员信息</div>
                </div>

                <!-- 执行人信息 -->
                <div class="bg-gradient-to-br from-purple-50 to-white border border-purple-100 rounded-xl p-4">
                  <div class="flex items-center justify-between mb-3">
                    <div class="flex items-center gap-2">
                      <div class="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                        <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                        </svg>
                      </div>
                      <span class="text-xs font-medium text-purple-600">执行人</span>
                    </div>
                    <!-- 更换执行人按钮：仅待确认(0)、执行中(1)、已驳回(4)状态可更换 -->
                    <el-popover
                      v-if="canChangeExecutor"
                      :visible="showExecutorPopover"
                      placement="bottom-end"
                      :width="280"
                    >
                      <template #reference>
                        <button
                          v-permission="'order:executor:change'"
                          @click="openExecutorPopover"
                          class="p-1 text-purple-400 hover:text-purple-600 hover:bg-purple-100 rounded transition-colors"
                          title="更换执行人"
                        >
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
                          </svg>
                        </button>
                      </template>
                      <div class="space-y-3">
                        <div class="text-sm font-medium text-slate-700">更换执行人</div>
                        <el-select
                          v-model="newExecutorId"
                          filterable
                          remote
                          clearable
                          reserve-keyword
                          placeholder="搜索并选择执行人"
                          :remote-method="handleExecutorSearch"
                          :loading="executorLoading"
                          class="w-full"
                          @visible-change="handleExecutorDropdownVisible"
                        >
                          <el-option
                            v-for="item in executorList"
                            :key="item.id"
                            :label="item.displayName || item.username"
                            :value="item.id"
                            :disabled="item.id === orderDetail?.executorId"
                          >
                            <div class="flex items-center justify-between">
                              <span>{{ item.displayName || item.username }}</span>
                              <span v-if="item.phone" class="text-xs text-slate-400 ml-2">{{ item.phone }}</span>
                            </div>
                          </el-option>
                          <template v-if="executorPagination.total > executorList.length" #footer>
                            <div class="border-t border-slate-200 py-2 text-center">
                              <button
                                type="button"
                                class="text-sm text-primary-600 hover:text-primary-700 disabled:opacity-50"
                                :disabled="executorLoading"
                                @click.stop="loadMoreExecutors"
                              >
                                {{ executorLoading ? '加载中...' : `加载更多 (${executorList.length}/${executorPagination.total})` }}
                              </button>
                            </div>
                          </template>
                        </el-select>
                        <div class="flex justify-end gap-2">
                          <button @click="closeExecutorPopover" class="px-3 py-1.5 text-sm bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200">取消</button>
                          <button :disabled="executorSubmitting || !newExecutorId" @click="handleChangeExecutor" class="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50">确认更换</button>
                        </div>
                      </div>
                    </el-popover>
                  </div>
                  <div v-if="orderDetail?.executor" class="space-y-2">
                    <div class="flex items-center gap-2">
                      <img
                        v-if="orderDetail.executor.avatarUrl"
                        :src="orderDetail.executor.avatarUrl"
                        class="w-8 h-8 rounded-full object-cover"
                        alt=""
                      />
                      <div v-else class="w-8 h-8 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                        {{ orderDetail.executor.displayName?.charAt(0) }}
                      </div>
                      <span class="font-semibold text-slate-900">{{ orderDetail.executor.displayName }}</span>
                    </div>
                    <!-- <div v-if="orderDetail.executor.roleNames?.length" class="flex flex-wrap gap-1">
                      <span v-for="role in orderDetail.executor.roleNames" :key="role" class="text-xs px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">{{ role }}</span>
                    </div> -->
                    <div class="flex items-center gap-1.5 text-sm text-slate-600">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                      </svg>
                      <span>{{ orderDetail.executor.phone || '-' }}</span>
                    </div>
                    <div v-if="orderDetail.executor.email" class="flex items-center gap-1.5 text-xs text-slate-500">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                      </svg>
                      <span>{{ orderDetail.executor.email }}</span>
                    </div>
                  </div>
                  <div v-else class="text-sm text-slate-400">暂无执行人信息</div>
                </div>
              </div>
            </div>

            <!-- 产品快照信息 -->
            <div v-if="orderDetail?.serviceScopeSnapshot" class="mb-6">
              <h4 class="text-sm font-medium text-slate-700 mb-3">适用对象/范围</h4>
              <div class="bg-slate-50 rounded-lg p-3 text-sm text-slate-600">{{ orderDetail.serviceScopeSnapshot }}</div>
            </div>

            <div v-if="orderDetail?.deliverablesSnapshot" class="mb-6">
              <h4 class="text-sm font-medium text-slate-700 mb-3">交付物</h4>
              <div class="bg-slate-50 rounded-lg p-3 text-sm text-slate-600">{{ orderDetail.deliverablesSnapshot }}</div>
            </div>

            <div v-if="orderDetail?.noticesSnapshot" class="mb-6">
              <h4 class="text-sm font-medium text-slate-700 mb-3">注意事项</h4>
              <div class="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">{{ orderDetail.noticesSnapshot }}</div>
            </div>

            <div v-if="materialList.length > 0" class="mb-6">
              <div class="flex items-center gap-2 mb-3">
                <h4 class="text-sm font-semibold text-slate-800">订单资料</h4>
                <span class="px-1.5 py-0.5 bg-slate-100 text-slate-600 text-xs font-medium rounded">{{ materialList.length }}</span>
              </div>

              <div class="space-y-2">
                <div
                  v-for="material in materialList"
                  :key="material.id"
                  :class="[
                    'flex items-center gap-4 p-3 rounded-lg border transition-all duration-150',
                    material.needReview
                      ? 'bg-amber-50/50 border-amber-200'
                      : material.isBound
                        ? 'bg-green-50/50 border-green-200'
                        : 'bg-slate-50/50 border-slate-200',
                    'hover:shadow-sm'
                  ]"
                >
                  <!-- 状态指示器 -->
                  <div class="shrink-0">
                    <div class="w-8 h-8 rounded-lg flex items-center justify-center"
                      :class="material.needReview ? 'bg-amber-100' : material.isBound ? 'bg-green-100' : 'bg-slate-100'"
                    >
                      <svg v-if="material.needReview" class="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                      </svg>
                      <svg v-else-if="material.isBound" class="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      <svg v-else class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101"/>
                      </svg>
                    </div>
                  </div>

                  <!-- 资料信息 -->
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 mb-1">
                      <h5 class="text-sm font-medium text-slate-900 truncate">{{ material.materialName || material.name || material.definitionName || material.definitionCode }}</h5>
                      <span class="shrink-0 text-xs text-slate-500">{{ material.definitionCode || '-' }}</span>
                      <span v-if="material.isRequired === true || material.isRequired === 1" class="shrink-0 text-xs px-1.5 py-0.5 bg-rose-100 text-rose-700 rounded">必填</span>
                      <span :class="['shrink-0 text-xs px-1.5 py-0.5 rounded-full font-medium', material.bindingStatusClass]">
                        {{ material.bindingStatusText }}
                      </span>
                    </div>
                    <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                      <span>绑定来源: {{ material.bindingSourceText }}</span>
                      <span>当前绑定: {{ material.currentMaterialName || '-' }}</span>
                      <span v-if="material.submitTime">提交: {{ $formatDateTime(material.submitTime) }}</span>
                      <span>{{ material.visibilityText }}</span>
                    </div>
                    <div v-if="material.reviewRemark" class="mt-1.5 text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded">
                      备注: {{ material.reviewRemark }}
                    </div>
                  </div>

                  <!-- 操作按钮 -->
                  <div class="shrink-0 flex items-center gap-1.5">
                    <button
                      v-if="material.currentUserMaterialId"
                      @click="openMaterialDetailDialog(material)"
                      class="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors cursor-pointer"
                      title="查看资料"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                      </svg>
                    </button>
                    <button
                      @click="openBindingDialog(material)"
                      class="p-1.5 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded transition-colors cursor-pointer"
                      :title="material.isBound ? '更换绑定' : '绑定资料'"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/>
                      </svg>
                    </button>
                    <button
                      v-if="material.isBound"
                      @click="openUnbindDialog(material)"
                      class="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors cursor-pointer"
                      title="解绑"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
                      </svg>
                    </button>
                    <template v-if="material.needReview">
                      <button
                        @click="openReviewDialog(material, true)"
                        class="p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition-colors cursor-pointer"
                        title="通过"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                        </svg>
                      </button>
                      <button
                        @click="openReviewDialog(material, false)"
                        class="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors cursor-pointer"
                        title="驳回"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                      </button>
                    </template>
                  </div>
                </div>
              </div>
            </div>

            <!-- 账单列表 -->
            <div v-if="orderDetail?.billings && orderDetail.billings.length > 0">
              <h4 class="text-sm font-semibold text-slate-800 mb-3">账单明细</h4>
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div
                  v-for="billing in orderDetail.billings"
                  :key="billing.id"
                  class="bg-gradient-to-br from-white to-slate-50 rounded-xl border border-slate-200/60 p-4 hover:shadow-md hover:border-slate-300/80 transition-all duration-200 group"
                >
                  <!-- 卡片头部 -->
                  <div class="flex items-start justify-between mb-3">
                    <div class="flex items-center gap-2">
                      <span class="px-2 py-0.5 rounded text-xs font-medium" :class="getBillingStatusClass(billing.status)">
                        {{ getBillingStatusText(billing.status) }}
                      </span>
                      <span class="text-xs text-slate-400">
                        {{ billing.billingType === 'ONCE' ? '一次性' : '周期性' }}
                        <template v-if="billing.periodType">
                          · {{ { MONTH: '月', QUARTER: '季', YEAR: '年' }[billing.periodType] }}
                        </template>
                        <template v-if="billing.periodIndex">
                          · 第{{ billing.periodIndex }}期
                        </template>
                      </span>
                    </div>
                    <div class="text-right">
                      <div class="text-xs text-slate-500 mb-0.5">应收金额</div>
                      <div class="text-lg font-bold text-slate-900">¥{{ billing.amount?.toLocaleString() }}</div>
                    </div>
                  </div>

                  <!-- 进度条 -->
                  <div class="mb-3">
                    <div class="flex items-center justify-between text-xs mb-1.5">
                      <span class="text-slate-500">回款进度</span>
                      <span class="font-medium text-slate-700">
                        {{ billing.amount > 0 ? ((billing.paidAmount / billing.amount) * 100).toFixed(0) : 0 }}%
                      </span>
                    </div>
                    <div class="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        class="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full transition-all duration-500"
                        :style="{ width: billing.amount > 0 ? `${(billing.paidAmount / billing.amount) * 100}%` : '0%' }"
                      />
                    </div>
                    <div class="flex justify-between text-xs text-slate-500 mt-1">
                      <span>已收 ¥{{ billing.paidAmount?.toLocaleString() || '0' }}</span>
                      <span>待收 ¥{{ (billing.amount - billing.paidAmount).toLocaleString() }}</span>
                    </div>
                  </div>

                  <!-- 信息行 -->
                  <div class="grid grid-cols-2 gap-2 mb-3 text-xs">
                    <div class="bg-white rounded-lg p-2 border border-slate-100">
                      <div class="text-slate-400 mb-0.5">应收日期</div>
                      <div class="font-medium text-slate-700">{{ billing.dueDate || '-' }}</div>
                    </div>
                    <div class="bg-white rounded-lg p-2 border border-slate-100">
                      <div class="text-slate-400 mb-0.5">收款时间</div>
                      <div class="font-medium text-slate-700">{{ $formatDateTime(billing.paidTime) || '-' }}</div>
                    </div>
                  </div>

                  <!-- 操作按钮 -->
                  <div class="flex items-center justify-between">
                    <span class="text-xs text-slate-400">{{ billing.payChannel || '-' }}</span>
                    <div class="flex items-center gap-2">
                      <template v-if="billing.status === 0 || billing.status === 2 || billing.status === '0' || billing.status === '2'">
                        <button
                          v-permission="'billing:receive'"
                          @click="handleReceive(billing)"
                          class="px-3 py-1.5 text-xs bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors flex items-center gap-1.5"
                        >
                          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          收款
                        </button>
                        <el-dropdown trigger="click" @command="(cmd) => handleCashierAction(cmd, billing)">
                          <button
                            :disabled="creatingCashierBillingId === billing.id"
                            class="px-3 py-1.5 text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-medium transition-colors disabled:opacity-60 flex items-center gap-1.5"
                          >
                            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 100 4 2 2 0 000-4z" />
                            </svg>
                            收银
                            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          <template #dropdown>
                            <el-dropdown-menu>
                              <el-dropdown-item command="open">
                                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                                收银台
                              </el-dropdown-item>
                              <el-dropdown-item command="copy">
                                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                                复制链接
                              </el-dropdown-item>
                            </el-dropdown-menu>
                          </template>
                        </el-dropdown>
                      </template>
                      <template v-else-if="billing.status === 1">
                        <span class="px-2 py-1 text-xs bg-emerald-100 text-emerald-700 rounded-lg font-medium flex items-center gap-1">
                          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                          </svg>
                          已支付
                        </span>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 订单任务列表 -->
            <div v-if="orderDetail?.tasks && orderDetail.tasks.length > 0" class="mb-6">
              <h4 class="text-sm font-semibold text-slate-800 mb-3">任务列表</h4>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div
                  v-for="task in orderDetail.tasks"
                  :key="task.id"
                  class="relative bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition-all duration-200"
                >
                  <!-- 排序号角标 -->
                  <div class="absolute -top-2 -left-2 w-7 h-7 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center shadow-lg">
                    <span class="text-xs font-bold text-white">{{ task.sort ?? '#' }}</span>
                  </div>

                  <!-- 任务头部 -->
                  <div class="flex items-start justify-between mb-3">
                    <div class="flex-1">
                      <div class="flex items-center gap-2 mb-1">
                        <h5 class="font-semibold text-slate-900">{{ task.name }}</h5>
                        <span :class="['px-2 py-0.5 rounded-full text-xs font-medium', getTaskStatusClass(task.status)]">
                          {{ task.statusDesc || getTaskStatusText(task.status) }}
                        </span>
                      </div>
                      <p v-if="task.type" class="text-xs text-slate-500">{{ task.type }}</p>
                    </div>
                    <button
                      @click="handleEditTask(task)"
                      class="ml-2 p-1.5 text-slate-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                      title="编辑任务"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                      </svg>
                    </button>
                  </div>

                  <!-- 进度条 -->
                  <div class="mb-3">
                    <div class="flex items-center justify-between text-xs text-slate-600 mb-1">
                      <span>任务进度</span>
                      <span class="font-semibold">{{ task.progress || 0 }}%</span>
                    </div>
                    <div class="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                      <div
                        :class="['h-full rounded-full transition-all duration-300', getProgressBarClass(task.status)]"
                        :style="{ width: `${task.progress || 0}%` }"
                      ></div>
                    </div>
                  </div>

                  <!-- 负责人 -->
                  <div v-if="task.assigneeName" class="flex items-center gap-2 mb-2">
                    <div class="w-6 h-6 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center text-white text-xs font-medium">
                      {{ task.assigneeName?.charAt(0) }}
                    </div>
                    <span class="text-sm text-slate-700">{{ task.assigneeName }}</span>
                  </div>

                  <!-- 时间信息 -->
                  <div class="flex flex-wrap gap-3 text-xs text-slate-500">
                    <div v-if="task.startTime" class="flex items-center gap-1">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      <span>{{ $formatDateTime(task.startTime) }}</span>
                    </div>
                    <div v-if="task.endTime" class="flex items-center gap-1">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      <span>{{ $formatDateTime(task.endTime) }}</span>
                    </div>
                  </div>

                  <!-- 备注 -->
                  <div v-if="task.remark" class="mt-2 pt-2 border-t border-slate-200">
                    <p class="text-xs text-slate-600 line-clamp-2">{{ task.remark }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- 服务跟踪 -->
            <div class="mt-6 mb-6">
              <!-- 服务步骤区标题 -->
              <div class="flex items-center justify-between mb-3">
                <span class="text-sm font-semibold text-slate-800">服务步骤</span>
                <button
                  @click="openAddStepDialog"
                  class="px-3 py-1.5 text-xs bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  + 添加步骤
                </button>
              </div>

              <!-- 服务步骤卡片列表 -->
              <div v-if="steps.length > 0" class="space-y-3">
                <div
                  v-for="(stepItem, index) in steps"
                  :key="stepItem.step.id"
                  class="border rounded-lg overflow-hidden"
                  :class="stepBorderClass(stepItem.step.status)"
                >
                  <!-- 步骤卡片头部（可点击展开/收起） -->
                  <div
                    class="flex items-center justify-between px-4 py-3 cursor-pointer select-none"
                    :class="stepHeaderBgClass(stepItem.step.status)"
                    @click="toggleStepExpand(stepItem.step.id)"
                  >
                    <div class="flex items-center gap-2">
                      <span class="w-1.5 h-1.5 rounded-full" :class="stepDotClass(stepItem.step.status)"></span>
                      <span
                        class="font-semibold text-sm"
                        :class="stepItem.step.status === 0 ? 'text-slate-400' : 'text-slate-900'"
                      >步骤 {{ index + 1 }} · {{ stepItem.step.name }}</span>
                      <span class="px-1.5 py-0.5 rounded text-xs font-medium" :class="stepStatusBadgeClass(stepItem.step.status)">
                        {{ stepStatusText(stepItem.step.status) }}
                      </span>
                    </div>
                    <span class="inline-flex items-center gap-1 text-xs text-slate-400">
                      {{ expandedStepIds.has(stepItem.step.id) ? '收起' : '展开' }}
                      <svg class="w-3 h-3 transition-transform duration-200" :class="{ 'rotate-180': expandedStepIds.has(stepItem.step.id) }" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
                    </span>
                  </div>

                  <!-- 步骤描述（始终显示） -->
                  <div
                    class="px-4 py-3 text-sm border-b border-slate-100"
                    :class="stepItem.step.status === 0 ? 'text-slate-400' : 'text-slate-600'"
                  >
                    {{ stepItem.step.content || '暂无描述' }}
                  </div>

                  <!-- 元信息（始终显示） -->
                  <div class="px-4 py-2.5 text-xs text-slate-400 flex flex-wrap items-center gap-x-4 gap-y-1">
                    <span v-if="stepItem.step.nextRemindAt" class="inline-flex items-center gap-1">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                      下次提醒：{{ stepItem.step.nextRemindAt }}
                    </span>
                    <span v-else class="inline-flex items-center gap-1">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                      无提醒
                    </span>
                    <span>创建人：{{ stepItem.step.createBy || '-' }}</span>
                    <span>{{ stepItem.step.createTime }}</span>
                  </div>

                  <!-- 展开内容：批注区 + 操作按钮 -->
                  <div v-if="expandedStepIds.has(stepItem.step.id)">
                    <!-- 步骤批注 -->
                    <div class="bg-slate-50 border-t border-slate-100">
                      <div class="px-4 py-3">
                        <div class="text-xs text-slate-400 mb-3">步骤批注</div>
                        <div class="space-y-3">
                          <div
                            v-for="ann in stepItem.annotations"
                            :key="ann.id"
                            class="flex items-start gap-2.5"
                          >
                            <div
                              class="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs shrink-0"
                              :style="{ backgroundColor: getAvatarColor(ann.operatorName) }"
                            >
                              {{ ann.operatorName?.charAt(0) || '?' }}
                            </div>
                            <div class="flex-1 min-w-0">
                              <div class="text-xs">
                                <strong class="text-slate-700">{{ ann.operatorName }}</strong>
                                <span class="text-slate-400 ml-1.5">{{ $formatDateTime(ann.createTime) }}</span>
                              </div>
                              <div class="text-sm text-slate-600 mt-1">{{ ann.content }}</div>
                            </div>
                          </div>
                          <div v-if="stepItem.annotations.length === 0" class="text-xs text-slate-400">
                            暂无批注
                          </div>
                        </div>
                        <button
                          @click="openAddStepAnnotationDialog(stepItem.step.id)"
                          class="mt-3 text-xs text-primary-600 hover:text-primary-700 px-2 py-1 rounded transition-colors"
                        >
                          + 添加批注
                        </button>
                      </div>
                    </div>
                    <!-- 操作按钮 -->
                    <div class="px-4 py-2.5 border-t border-slate-100 flex items-center gap-2">
                      <template v-if="stepItem.step.status === 0">
                        <button
                          @click="handleStepStatusChange(stepItem.step.id, 1)"
                          class="px-3 py-1 text-xs bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors"
                        >标记进行中</button>
                      </template>
                      <template v-if="stepItem.step.status === 1">
                        <button
                          @click="handleStepStatusChange(stepItem.step.id, 2)"
                          class="px-3 py-1 text-xs bg-green-50 text-green-600 rounded hover:bg-green-100 transition-colors"
                        >标记完成</button>
                      </template>
                      <template v-if="stepItem.step.status === 2">
                        <button
                          @click="handleStepStatusChange(stepItem.step.id, 1)"
                          class="px-3 py-1 text-xs bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors"
                        >重新打开</button>
                      </template>
                      <button
                        @click="openEditStepDialog(stepItem.step)"
                        class="px-3 py-1 text-xs bg-slate-100 text-slate-600 rounded hover:bg-slate-200 transition-colors"
                      >编辑步骤</button>
                      <button
                        @click="handleDeleteStep(stepItem.step.id)"
                        class="px-3 py-1 text-xs bg-red-50 text-red-600 rounded hover:bg-red-100 transition-colors"
                      >删除</button>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="data-table-empty is-compact">
                <div class="text-sm text-slate-500">暂无服务步骤</div>
                <button
                  @click="openAddStepDialog"
                  class="mt-2 text-xs text-primary-600 hover:text-primary-700"
                >
                  点击上方按钮添加
                </button>
              </div>
            </div>

            <!-- 订单提醒 -->
            <div class="mb-6">
              <div class="flex items-center justify-between mb-3">
                <div class="flex items-center gap-2">
                  <h4 class="text-sm font-semibold text-slate-800">订单提醒</h4>
                  <span class="px-1.5 py-0.5 bg-slate-100 text-slate-600 text-xs font-medium rounded">{{ orderReminderList.length }}</span>
                </div>
                <button
                  @click="openAddReminderDialog"
                  class="px-3 py-1.5 text-xs bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  + 添加提醒
                </button>
              </div>

              <div v-if="orderReminderList.length > 0" class="space-y-2">
                <div
                  v-for="reminder in orderReminderList"
                  :key="reminder.id"
                  class="rounded-lg border border-slate-200 bg-white p-3 hover:shadow-sm transition-shadow"
                >
                  <div class="flex items-start justify-between gap-3">
                    <div class="min-w-0 flex-1">
                      <div class="flex flex-wrap items-center gap-2">
                        <h5 class="text-sm font-semibold text-slate-900 truncate">{{ reminder.title }}</h5>
                        <span :class="['shrink-0 px-1.5 py-0.5 rounded-full text-xs font-medium', reminderStatusClass(reminder.status)]">
                          {{ reminder.statusDesc || reminderStatusText(reminder.status) }}
                        </span>
                        <span v-if="reminder.repeatEnabled" class="shrink-0 px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                          重复
                        </span>
                      </div>
                      <div v-if="reminder.content" class="mt-1 text-sm text-slate-600 line-clamp-2">{{ reminder.content }}</div>
                      <div class="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                        <span>首次提醒: {{ $formatDateTime(reminder.firstSendTime) }}</span>
                        <span>{{ reminderRepeatText(reminder) }}</span>
                        <span v-if="reminder.deadlineTime">截止: {{ $formatDateTime(reminder.deadlineTime) }}</span>
                      </div>
                      <div class="mt-2 flex flex-wrap items-center gap-1.5">
                        <span class="text-xs text-slate-400 mr-1">接收人</span>
                        <span
                          v-for="receiver in reminder.receivers || []"
                          :key="receiver.userId"
                          class="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs"
                        >
                          {{ receiver.userName || `用户${receiver.userId}` }}
                        </span>
                        <span v-if="!reminder.receivers?.length" class="text-xs text-slate-400">未配置</span>
                      </div>
                    </div>
                    <div class="shrink-0 flex items-center gap-1">
                      <button
                        @click="openEditReminderDialog(reminder)"
                        class="px-2 py-1 text-xs bg-slate-100 text-slate-600 rounded hover:bg-slate-200 transition-colors"
                      >编辑</button>
                      <button
                        v-if="reminder.status === 'ENABLED'"
                        @click="handleToggleReminder(reminder)"
                        class="px-2 py-1 text-xs bg-amber-50 text-amber-700 rounded hover:bg-amber-100 transition-colors"
                      >停用</button>
                      <button
                        v-else
                        @click="handleToggleReminder(reminder)"
                        class="px-2 py-1 text-xs bg-green-50 text-green-700 rounded hover:bg-green-100 transition-colors"
                      >启用</button>
                      <button
                        @click="handleDeleteReminder(reminder)"
                        class="px-2 py-1 text-xs bg-red-50 text-red-600 rounded hover:bg-red-100 transition-colors"
                      >删除</button>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="data-table-empty is-compact">
                <div class="text-sm text-slate-500">暂无订单提醒</div>
                <button
                  @click="openAddReminderDialog"
                  class="mt-2 text-xs text-primary-600 hover:text-primary-700"
                >
                  添加一个提醒
                </button>
              </div>
            </div>

          </div>
        </div>

        <div class="px-6 py-4 border-t border-slate-200">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <!-- 待确认状态下显示审批操作 -->
              <template v-if="orderDetail?.status === 0">
                <el-popover
                  :visible="showApprovePopover"
                  placement="top"
                  :width="240"
                >
                  <template #reference>
                    <button
                      v-permission="'order:approve'"
                      @click="showApprovePopover = true"
                      class="h-10 px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 inline-flex items-center justify-center gap-2 transition-colors"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                      </svg>
                      <span>通过</span>
                    </button>
                  </template>
                  <div class="space-y-3">
                    <div class="text-sm font-medium text-slate-700">确认通过该订单？</div>
                    <div class="flex justify-end gap-2">
                      <button @click="showApprovePopover = false" class="px-3 py-1.5 text-sm bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200">取消</button>
                      <button :disabled="approvalSubmitting" @click="handleApprove(true)" class="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">确认通过</button>
                    </div>
                  </div>
                </el-popover>
                <el-popover
                  :visible="showRejectPopover"
                  placement="top"
                  :width="280"
                >
                  <template #reference>
                    <button
                      v-permission="'order:approve'"
                      @click="showRejectPopover = true"
                      class="h-10 px-4 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 inline-flex items-center justify-center gap-2 transition-colors"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                      </svg>
                      <span>驳回</span>
                    </button>
                  </template>
                  <div class="space-y-3">
                    <div class="text-sm font-medium text-slate-700">请填写驳回原因</div>
                    <el-input
                      v-model="rejectReason"
                      type="textarea"
                      :rows="3"
                      placeholder="请输入原因"
                      maxlength="200"
                      show-word-limit
                    />
                    <div class="flex justify-end gap-2">
                      <button @click="closeRejectPopover" class="px-3 py-1.5 text-sm bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200">取消</button>
                      <button :disabled="approvalSubmitting" @click="handleApprove(false)" class="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">确认驳回</button>
                    </div>
                  </div>
                </el-popover>
              </template>
            </div>
            <div class="flex items-center gap-3">
              <!-- 编辑按钮：非已完成(2)、非已取消(3)状态下显示 -->
              <button
                v-if="orderDetail?.status !== 2 && orderDetail?.status !== 3"
                v-permission="'order:edit'"
                @click="handleEdit"
                class="h-10 px-5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 inline-flex items-center justify-center gap-2 transition-colors"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                </svg>
                <span>编辑</span>
              </button>
              <button @click="handleClose" class="h-10 px-5 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 inline-flex items-center justify-center">关闭</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <el-dialog
    v-model="bindingDialogVisible"
    title="选择资料"
    width="960px"
    destroy-on-close
    @close="closeBindingDialog"
  >
    <div class="space-y-4">
      <div class="rounded-lg bg-slate-50 p-4 text-sm text-slate-600">
        <div>订单材料：{{ bindingTargetMaterial?.materialName || bindingTargetMaterial?.definitionName || '-' }}</div>
        <div class="mt-1">资料类型：{{ bindingTargetMaterial?.definitionCode || '-' }}</div>
        <div class="mt-1 text-xs text-slate-500">仅展示当前订单材料类型匹配的客户资料实例</div>
      </div>

      <div class="flex flex-col gap-3 md:flex-row">
        <el-input v-model="candidateKeyword" clearable placeholder="按资料名称或类型搜索" />
        <el-select v-model="candidateVisibilityFilter" clearable placeholder="客户可见性" style="width: 180px">
          <el-option label="全部" value="" />
          <el-option label="对客户可见" value="visible" />
          <el-option label="对客户隐藏" value="hidden" />
        </el-select>
      </div>

      <div v-loading="bindingDialogLoading" class="max-h-[420px] overflow-y-auto rounded-lg border border-slate-200">
        <table class="min-w-full text-sm">
          <thead class="bg-slate-50 text-slate-500">
            <tr>
              <th class="px-4 py-3 text-left font-medium">选择</th>
              <th class="px-4 py-3 text-left font-medium">资料名称</th>
              <th class="px-4 py-3 text-left font-medium">资料类型</th>
              <th class="px-4 py-3 text-left font-medium">最后更新时间</th>
              <th class="px-4 py-3 text-left font-medium">客户可见性</th>
              <th class="px-4 py-3 text-left font-medium">绑定状态</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="candidate in filteredMaterialCandidates" :key="candidate.id" class="border-t border-slate-100">
              <td class="px-4 py-3">
                <el-radio v-model="selectedCandidateId" :label="candidate.id">
                  <span></span>
                </el-radio>
              </td>
              <td class="px-4 py-3 text-slate-900">{{ candidate.name || '-' }}</td>
              <td class="px-4 py-3 text-slate-600">{{ candidate.definitionName || candidate.definitionCode || '-' }}</td>
              <td class="px-4 py-3 text-slate-600">{{ $formatDateTime(candidate.updateTime || candidate.createTime) }}</td>
              <td class="px-4 py-3">
                <span
                  :class="[
                    'rounded-full px-2 py-0.5 text-xs font-medium',
                    candidate.visibleToCustomer ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                  ]"
                >
                  {{ getCandidateVisibilityText(candidate.visibleToCustomer) }}
                </span>
              </td>
              <td class="px-4 py-3 text-slate-600">
                {{ candidate.isCurrentBinding ? '当前已绑定' : '-' }}
              </td>
            </tr>
            <tr v-if="!bindingDialogLoading && filteredMaterialCandidates.length === 0">
              <td colspan="6" class="px-4 py-8 text-center text-slate-500">暂无可选资料</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <template #footer>
      <div class="flex justify-end gap-3">
        <button class="px-4 py-2 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200" @click="closeBindingDialog">取消</button>
        <button
          :disabled="bindingSubmitting || !selectedCandidateId"
          class="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          @click="handleConfirmBinding"
        >
          {{ bindingSubmitting ? '提交中...' : '确认绑定' }}
        </button>
      </div>
    </template>
  </el-dialog>

  <el-dialog
    v-model="unbindDialogVisible"
    title="解绑资料"
    width="520px"
    destroy-on-close
    @close="closeUnbindDialog"
  >
    <div class="space-y-4">
      <div class="rounded-lg bg-slate-50 p-4 text-sm text-slate-600">
        确认解绑“{{ unbindTargetMaterial?.materialName || unbindTargetMaterial?.definitionName || '-' }}”当前绑定的资料吗？
      </div>
      <el-input
        v-model="unbindReason"
        type="textarea"
        :rows="4"
        maxlength="200"
        show-word-limit
        placeholder="请输入解绑原因，便于后续追溯"
      />
    </div>
    <template #footer>
      <div class="flex justify-end gap-3">
        <button class="px-4 py-2 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200" @click="closeUnbindDialog">取消</button>
        <button
          :disabled="unbindSubmitting"
          class="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
          @click="handleConfirmUnbind"
        >
          {{ unbindSubmitting ? '解绑中...' : '确认解绑' }}
        </button>
      </div>
    </template>
  </el-dialog>

  <el-dialog
    v-model="reviewDialogVisible"
    :title="reviewForm.approved ? '审核通过' : '驳回绑定'"
    width="520px"
    destroy-on-close
    @close="closeReviewDialog"
  >
    <div class="space-y-4">
      <div class="rounded-lg bg-slate-50 p-4 text-sm text-slate-600">
        {{ reviewTargetMaterial?.materialName || reviewTargetMaterial?.definitionName || '-' }} 当前由客户在 APP 侧提交绑定。
      </div>
      <el-input
        v-model="reviewForm.reviewRemark"
        type="textarea"
        :rows="4"
        maxlength="200"
        show-word-limit
        :placeholder="reviewForm.approved ? '可选填写审核备注' : '请输入驳回原因'"
      />
    </div>
    <template #footer>
      <div class="flex justify-end gap-3">
        <button class="px-4 py-2 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200" @click="closeReviewDialog">取消</button>
        <button
          :disabled="reviewSubmitting"
          :class="[
            'px-4 py-2 text-sm text-white rounded-lg disabled:opacity-50',
            reviewForm.approved ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
          ]"
          @click="handleSubmitReview"
        >
          {{ reviewSubmitting ? '提交中...' : reviewForm.approved ? '确认通过' : '确认驳回' }}
        </button>
      </div>
    </template>
  </el-dialog>

  <OrderMaterialDetailDialog
    v-model:visible="materialDetailDialogVisible"
    :user-material-id="selectedMaterialDetailId"
  />

  <el-dialog
    v-model="reminderDialogVisible"
    :title="reminderDialogTitle"
    width="680px"
    destroy-on-close
    @close="closeReminderDialog"
  >
    <div class="space-y-4">
      <div>
        <label class="block text-sm font-medium text-slate-700 mb-1">提醒标题 <span class="text-red-500">*</span></label>
        <el-input v-model="reminderForm.title" placeholder="请输入提醒标题" maxlength="100" show-word-limit />
      </div>
      <div>
        <label class="block text-sm font-medium text-slate-700 mb-1">提醒内容</label>
        <el-input
          v-model="reminderForm.content"
          type="textarea"
          :rows="3"
          placeholder="请输入提醒内容"
          maxlength="500"
          show-word-limit
        />
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-slate-700 mb-1">首次提醒时间 <span class="text-red-500">*</span></label>
          <el-input v-model="reminderForm.firstSendTime" type="datetime-local" />
        </div>
        <div>
          <label class="block text-sm font-medium text-slate-700 mb-1">截止时间</label>
          <el-input v-model="reminderForm.deadlineTime" type="datetime-local" />
        </div>
      </div>
      <div class="rounded-lg border border-slate-200 p-3">
        <label class="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
          <input v-model="reminderForm.repeatEnabled" type="checkbox" class="w-4 h-4 rounded" />
          <span>重复提醒</span>
        </label>
        <div v-if="reminderForm.repeatEnabled" class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
          <div>
            <label class="block text-xs text-slate-500 mb-1">重复间隔（分钟）</label>
            <el-input v-model.number="reminderForm.repeatIntervalMinutes" type="number" min="1" />
          </div>
          <div>
            <label class="block text-xs text-slate-500 mb-1">最大提醒次数</label>
            <el-input v-model.number="reminderForm.maxReminderCount" type="number" min="1" />
          </div>
        </div>
        <label class="mt-3 flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
          <input v-model="reminderForm.autoCloseAtDeadline" type="checkbox" class="w-4 h-4 rounded" />
          <span>到截止时间自动关闭提醒</span>
        </label>
      </div>
      <div>
        <label class="block text-sm font-medium text-slate-700 mb-2">提醒接收人 <span class="text-red-500">*</span></label>
        <div v-if="orderRelatedReminderUsers.length > 0" class="mb-3">
          <div class="text-xs text-slate-400 mb-2">订单关联人员</div>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="user in orderRelatedReminderUsers"
              :key="user.id"
              type="button"
              :class="[
                'px-3 py-1.5 rounded-full border text-xs transition-colors',
                isReminderReceiverSelected(user.id)
                  ? 'border-primary-200 bg-primary-50 text-primary-700'
                  : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
              ]"
              @click="toggleReminderReceiver(user.id)"
            >
              {{ user.displayName || user.username || `用户${user.id}` }}
              <span v-if="user.roles.length" class="text-slate-400 ml-1">{{ user.roles.join('/') }}</span>
            </button>
          </div>
        </div>
        <el-select
          v-model="reminderForm.receiverUserIds"
          multiple
          filterable
          remote
          clearable
          reserve-keyword
          placeholder="搜索并选择其他提醒人员"
          :remote-method="handleReminderUserSearch"
          :loading="reminderUserLoading"
          class="w-full"
          @visible-change="handleReminderUserDropdownVisible"
        >
          <el-option
            v-for="item in reminderUserOptions"
            :key="item.id"
            :label="item.displayName || item.username || `用户${item.id}`"
            :value="item.id"
          >
            <div class="flex items-center justify-between">
              <span>{{ item.displayName || item.username || `用户${item.id}` }}</span>
              <span v-if="item.phone" class="text-xs text-slate-400 ml-2">{{ item.phone }}</span>
            </div>
          </el-option>
          <template v-if="reminderUserPagination.total > reminderUserList.length" #footer>
            <div class="border-t border-slate-200 py-2 text-center">
              <button
                type="button"
                class="text-sm text-primary-600 hover:text-primary-700 disabled:opacity-50"
                :disabled="reminderUserLoading"
                @click.stop="loadMoreReminderUsers"
              >
                {{ reminderUserLoading ? '加载中...' : `加载更多 (${reminderUserList.length}/${reminderUserPagination.total})` }}
              </button>
            </div>
          </template>
        </el-select>
      </div>
    </div>
    <template #footer>
      <div class="flex justify-end gap-3">
        <button class="px-4 py-2 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200" @click="closeReminderDialog">取消</button>
        <button
          :disabled="reminderSubmitting"
          class="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          @click="handleSubmitReminder"
        >
          {{ reminderSubmitting ? '提交中...' : '确认' }}
        </button>
      </div>
    </template>
  </el-dialog>

  <!-- 步骤编辑弹窗 -->
  <el-dialog
    v-model="stepDialogVisible"
    :title="stepDialogTitle"
    width="520px"
    destroy-on-close
    @close="closeStepDialog"
  >
    <div class="space-y-4">
      <div>
        <label class="block text-sm font-medium text-slate-700 mb-1">步骤名称 <span class="text-red-500">*</span></label>
        <el-input v-model="stepForm.name" placeholder="请输入步骤名称" maxlength="100" show-word-limit />
      </div>
      <div>
        <label class="block text-sm font-medium text-slate-700 mb-1">步骤内容 <span class="text-red-500">*</span></label>
        <el-input
          v-model="stepForm.content"
          type="textarea"
          :rows="3"
          placeholder="请输入步骤内容/描述"
          maxlength="500"
          show-word-limit
        />
      </div>
      <div v-if="stepForm.id">
        <label class="block text-sm font-medium text-slate-700 mb-1">状态</label>
        <el-select v-model="stepForm.status" class="w-full">
          <el-option label="待处理" :value="0" />
          <el-option label="进行中" :value="1" />
          <el-option label="已完成" :value="2" />
        </el-select>
      </div>
      <div class="flex items-center gap-3">
        <label class="flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer">
          <input v-model="stepForm.enableReminder" type="checkbox" class="w-4 h-4 rounded" />
          <span>设置提醒</span>
        </label>
      </div>
      <div v-if="stepForm.enableReminder" class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-slate-700 mb-1">提醒时间</label>
          <el-input
            v-model="stepForm.nextRemindAt"
            type="datetime-local"
            placeholder="选择提醒时间"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-slate-700 mb-2">提醒接收人</label>
          <div v-if="orderRelatedReminderUsers.length > 0" class="mb-3">
            <div class="text-xs text-slate-400 mb-2">订单关联人员</div>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="user in orderRelatedReminderUsers"
                :key="user.id"
                type="button"
                :class="[
                  'px-3 py-1.5 rounded-full border text-xs transition-colors',
                  stepForm.remindUserIds.includes(user.id)
                    ? 'border-primary-200 bg-primary-50 text-primary-700'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                ]"
                @click="toggleStepRemindUser(user.id)"
              >
                {{ user.displayName || user.username || `用户${user.id}` }}
                <span v-if="user.roles.length" class="text-slate-400 ml-1">{{ user.roles.join('/') }}</span>
              </button>
            </div>
          </div>
          <el-select
            v-model="stepForm.remindUserIds"
            multiple
            filterable
            remote
            clearable
            reserve-keyword
            placeholder="搜索并选择提醒人员"
            :remote-method="handleReminderUserSearch"
            :loading="reminderUserLoading"
            class="w-full"
            @visible-change="handleReminderUserDropdownVisible"
          >
            <el-option
              v-for="item in reminderUserOptions"
              :key="item.id"
              :label="item.displayName || item.username || `用户${item.id}`"
              :value="item.id"
            >
              <div class="flex items-center justify-between">
                <span>{{ item.displayName || item.username || `用户${item.id}` }}</span>
                <span v-if="item.phone" class="text-xs text-slate-400 ml-2">{{ item.phone }}</span>
              </div>
            </el-option>
            <template v-if="reminderUserPagination.total > reminderUserList.length" #footer>
              <div class="border-t border-slate-200 py-2 text-center">
                <button
                  type="button"
                  class="text-sm text-primary-600 hover:text-primary-700 disabled:opacity-50"
                  :disabled="reminderUserLoading"
                  @click.stop="loadMoreReminderUsers"
                >
                  {{ reminderUserLoading ? '加载中...' : `加载更多 (${reminderUserList.length}/${reminderUserPagination.total})` }}
                </button>
              </div>
            </template>
          </el-select>
        </div>
      </div>
    </div>
    <template #footer>
      <div class="flex justify-end gap-3">
        <button class="px-4 py-2 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200" @click="closeStepDialog">取消</button>
        <button
          :disabled="stepSubmitting || !stepForm.name || !stepForm.content"
          class="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          @click="handleSubmitStep"
        >
          {{ stepSubmitting ? '提交中...' : '确认' }}
        </button>
      </div>
    </template>
  </el-dialog>

  <!-- 批注编辑弹窗 -->
  <el-dialog
    v-model="annotationDialogVisible"
    title="添加批注"
    width="480px"
    destroy-on-close
    @close="closeAnnotationDialog"
  >
    <div class="space-y-4">
      <div>
        <label class="block text-sm font-medium text-slate-700 mb-1">批注内容 <span class="text-red-500">*</span></label>
        <el-input
          v-model="annotationForm.content"
          type="textarea"
          :rows="4"
          placeholder="请输入批注内容"
          maxlength="500"
          show-word-limit
        />
      </div>
      <div class="flex items-center gap-3">
        <label class="flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer">
          <input v-model="annotationForm.enableReminder" type="checkbox" class="w-4 h-4 rounded" />
          <span>设置提醒</span>
        </label>
      </div>
      <div v-if="annotationForm.enableReminder" class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-slate-700 mb-1">提醒时间</label>
          <el-input
            v-model="annotationForm.nextRemindAt"
            type="datetime-local"
            placeholder="选择提醒时间"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-slate-700 mb-2">提醒接收人</label>
          <div v-if="orderRelatedReminderUsers.length > 0" class="mb-3">
            <div class="text-xs text-slate-400 mb-2">订单关联人员</div>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="user in orderRelatedReminderUsers"
                :key="user.id"
                type="button"
                :class="[
                  'px-3 py-1.5 rounded-full border text-xs transition-colors',
                  annotationForm.remindUserIds.includes(user.id)
                    ? 'border-primary-200 bg-primary-50 text-primary-700'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                ]"
                @click="toggleAnnotationRemindUser(user.id)"
              >
                {{ user.displayName || user.username || `用户${user.id}` }}
                <span v-if="user.roles.length" class="text-slate-400 ml-1">{{ user.roles.join('/') }}</span>
              </button>
            </div>
          </div>
          <el-select
            v-model="annotationForm.remindUserIds"
            multiple
            filterable
            remote
            clearable
            reserve-keyword
            placeholder="搜索并选择提醒人员"
            :remote-method="handleReminderUserSearch"
            :loading="reminderUserLoading"
            class="w-full"
            @visible-change="handleReminderUserDropdownVisible"
          >
            <el-option
              v-for="item in reminderUserOptions"
              :key="item.id"
              :label="item.displayName || item.username || `用户${item.id}`"
              :value="item.id"
            >
              <div class="flex items-center justify-between">
                <span>{{ item.displayName || item.username || `用户${item.id}` }}</span>
                <span v-if="item.phone" class="text-xs text-slate-400 ml-2">{{ item.phone }}</span>
              </div>
            </el-option>
            <template v-if="reminderUserPagination.total > reminderUserList.length" #footer>
              <div class="border-t border-slate-200 py-2 text-center">
                <button
                  type="button"
                  class="text-sm text-primary-600 hover:text-primary-700 disabled:opacity-50"
                  :disabled="reminderUserLoading"
                  @click.stop="loadMoreReminderUsers"
                >
                  {{ reminderUserLoading ? '加载中...' : `加载更多 (${reminderUserList.length}/${reminderUserPagination.total})` }}
                </button>
              </div>
            </template>
          </el-select>
        </div>
      </div>
    </div>
    <template #footer>
      <div class="flex justify-end gap-3">
        <button class="px-4 py-2 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200" @click="closeAnnotationDialog">取消</button>
        <button
          :disabled="annotationSubmitting || !annotationForm.content"
          class="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          @click="handleSubmitAnnotation"
        >
          {{ annotationSubmitting ? '提交中...' : '提交批注' }}
        </button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { getUserList } from '@/api/user'
import { bindOrderMaterial, changeOrderExecutor, createOrderAnnotation, createOrderReminder, createServiceStep, deleteOrderReminder, deleteServiceStep, disableOrderReminder, enableOrderReminder, getOrderTracking, reviewOrderMaterialBinding, unbindOrderMaterial, updateOrderReminder, updateServiceStep, updateStepStatus } from '@/api/order'
import { getCustomerMaterialDetailById, listCustomerMaterialInstances, updateCustomerMaterialVisibility } from '@/api/customer'
import { createOrGetCashierByBilling } from '@/api/cashier'
import OrderMaterialDetailDialog from './OrderMaterialDetailDialog.vue'

// eslint-disable-next-line no-unused-vars
const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  orderDetail: {
    type: Object,
    default: () => null
  },
  traceList: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['update:visible', 'close', 'receive', 'editTask', 'edit', 'approve', 'approval-detail', 'executorChanged', 'refreshDetail'])

// 审批相关状态
const showApprovePopover = ref(false)
const showRejectPopover = ref(false)
const rejectReason = ref('')
const approvalSubmitting = ref(false)

// 更换执行人相关状态
const showExecutorPopover = ref(false)
const newExecutorId = ref(null)
const executorList = ref([])
const executorLoading = ref(false)
const executorSubmitting = ref(false)
const executorSearchKeyword = ref('')
const executorPagination = reactive({
  page: 1,
  size: 20,
  total: 0
})

const bindingDialogVisible = ref(false)
const bindingDialogLoading = ref(false)
const bindingSubmitting = ref(false)
const bindingTargetMaterial = ref(null)
const candidateKeyword = ref('')
const candidateVisibilityFilter = ref('')
const materialCandidates = ref([])
const selectedCandidateId = ref(null)

const unbindDialogVisible = ref(false)
const unbindSubmitting = ref(false)
const unbindReason = ref('')
const unbindTargetMaterial = ref(null)

const reviewDialogVisible = ref(false)
const reviewSubmitting = ref(false)
const reviewForm = reactive({
  approved: true,
  reviewRemark: ''
})
const reviewTargetMaterial = ref(null)
const materialDetailDialogVisible = ref(false)
const selectedMaterialDetailId = ref(null)
const creatingCashierBillingId = ref(null)

// ── 服务跟踪状态 ──
const steps = ref([])
const expandedStepIds = ref(new Set())
const trackingLoading = ref(false)

// 头像颜色（根据名字哈希取色）
const avatarColors = ['#1890ff', '#52c41a', '#fa8c16', '#722ed1', '#eb2f96', '#faad14', '#13c2c2']
const getAvatarColor = (name) => {
  if (!name) return avatarColors[0]
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return avatarColors[Math.abs(hash) % avatarColors.length]
}

// 步骤弹窗状态
const stepDialogVisible = ref(false)
const stepSubmitting = ref(false)
const stepForm = reactive({
  id: null,
  name: '',
  content: '',
  status: 0,
  enableReminder: false,
  nextRemindAt: '',
  remindUserIds: []
})
const isEditingStep = ref(false)

// 批注弹窗状态
const annotationDialogVisible = ref(false)
const annotationSubmitting = ref(false)
const annotationTargetStepId = ref(null)
const annotationForm = reactive({
  content: '',
  enableReminder: false,
  nextRemindAt: '',
  remindUserIds: []
})

const reminderDialogVisible = ref(false)
const reminderSubmitting = ref(false)
const isEditingReminder = ref(false)
const reminderForm = reactive({
  id: null,
  title: '',
  content: '',
  firstSendTime: '',
  repeatEnabled: false,
  repeatIntervalMinutes: 1440,
  maxReminderCount: 3,
  deadlineTime: '',
  autoCloseAtDeadline: false,
  receiverUserIds: []
})
const reminderUserKeyword = ref('')
const reminderUserLoading = ref(false)
const reminderUserList = ref([])
const reminderUserPagination = reactive({
  page: 1,
  size: 20,
  total: 0
})

const materialStatusMap = {
  UNBOUND: { text: '未绑定', class: 'bg-slate-100 text-slate-600' },
  PENDING: { text: '待审核', class: 'bg-amber-100 text-amber-700' },
  APPROVED: { text: '已绑定', class: 'bg-green-100 text-green-700' },
  REJECTED: { text: '已驳回', class: 'bg-red-100 text-red-700' },
  INVALID: { text: '已解绑', class: 'bg-slate-200 text-slate-500' }
}

const materialBindSourceMap = {
  APP: '客户APP绑定',
  ADMIN: '平台绑定',
  SYSTEM: '系统绑定'
}

const materialList = computed(() => {
  return (props.orderDetail?.materials || []).map(item => {
    const currentBinding = item.currentBinding || {}
    const currentMaterialName = item.currentMaterialName || currentBinding.userMaterialName || '-'
    const visibleToCustomer = item.currentMaterialVisibleToCustomer
    return {
      ...item,
      currentBinding,
      isBound: !!item.currentBindingId,
      currentMaterialName,
      bindingStatusText: materialStatusMap[item.bindingStatus]?.text || (item.currentBindingId ? '已绑定' : '未绑定'),
      bindingStatusClass: materialStatusMap[item.bindingStatus]?.class || 'bg-slate-100 text-slate-600',
      bindingSourceText: materialBindSourceMap[currentBinding.bindSource] || '-',
      currentMaterialVisibleToCustomer: visibleToCustomer,
      visibilityText: visibleToCustomer === true ? '对客户可见' : visibleToCustomer === false ? '对客户隐藏' : '-',
      needReview: currentBinding.bindSource === 'APP' && item.bindingStatus === 'PENDING',
      submitTime: currentBinding.submitTime || currentBinding.bindTime || currentBinding.createTime || null,
      reviewRemark: currentBinding.reviewRemark || ''
    }
  })
})

const filteredMaterialCandidates = computed(() => {
  const keyword = candidateKeyword.value.trim().toLowerCase()
  return materialCandidates.value.filter(item => {
    if (candidateVisibilityFilter.value === 'visible' && item.visibleToCustomer !== true) {
      return false
    }
    if (candidateVisibilityFilter.value === 'hidden' && item.visibleToCustomer !== false) {
      return false
    }
    if (!keyword) {
      return true
    }
    return [
      item.name,
      item.definitionName,
      item.definitionCode
    ].some(value => String(value || '').toLowerCase().includes(keyword))
  })
})

const orderReminderList = computed(() => props.orderDetail?.reminders || [])

const reminderDialogTitle = computed(() => isEditingReminder.value ? '编辑订单提醒' : '添加订单提醒')

const orderRelatedReminderUsers = computed(() => {
  const map = new Map()
  const addUser = (user, role) => {
    const id = user?.id
    if (!id) return
    if (!map.has(id)) {
      map.set(id, {
        id,
        displayName: user.displayName || user.username || `用户${id}`,
        username: user.username,
        phone: user.phone,
        roles: []
      })
    }
    const current = map.get(id)
    if (!current.roles.includes(role)) {
      current.roles.push(role)
    }
  }
  addUser(props.orderDetail?.createUser, '创建人')
  addUser(props.orderDetail?.executor, '执行人')
  return Array.from(map.values())
})

const reminderUserOptions = computed(() => {
  const map = new Map()
  for (const user of orderRelatedReminderUsers.value) {
    map.set(user.id, user)
  }
  for (const user of reminderUserList.value) {
    map.set(user.id, {
      ...user,
      displayName: user.displayName || user.username || `用户${user.id}`,
      roles: map.get(user.id)?.roles || []
    })
  }
  for (const reminder of orderReminderList.value) {
    for (const receiver of reminder.receivers || []) {
      if (!map.has(receiver.userId)) {
        map.set(receiver.userId, {
          id: receiver.userId,
          displayName: receiver.userName || `用户${receiver.userId}`,
          username: receiver.userName,
          roles: []
        })
      }
    }
  }
  return Array.from(map.values())
})

// 是否可以更换执行人：仅待确认(0)、执行中(1)、已驳回(4)状态可更换
const canChangeExecutor = computed(() => {
  const status = props.orderDetail?.status
  return status === 0 || status === 1 || status === 4
})

const handleClose = () => {
  materialDetailDialogVisible.value = false
  selectedMaterialDetailId.value = null
  emit('update:visible', false)
  emit('close')
}

const handleReceive = (billing) => {
  console.log('handleReceive called', billing)
  emit('receive', billing)
}

const openCashierPage = async (billing) => {
  if (!billing?.id) return
  creatingCashierBillingId.value = billing.id
  try {
    const created = await createOrGetCashierByBilling(Number(billing.id))
    const cashierNo = created?.cashierNo || ''
    if (!cashierNo) {
      ElMessage.error('收银台创建失败，请稍后重试')
      return
    }
    billing.cashierNo = cashierNo
    const targetUrl = `${window.location.origin}/cashier/${cashierNo}`
    window.open(targetUrl, '_blank')
  } catch (error) {
    ElMessage.error(error?.message || '创建收银台失败，请稍后重试')
  } finally {
    creatingCashierBillingId.value = null
  }
}

const handleCashierAction = (cmd, billing) => {
  if (cmd === 'open') {
    openCashierPage(billing)
  } else if (cmd === 'copy') {
    copyCashierLink(billing)
  }
}

const copyCashierLink = async (billing) => {
  if (!billing?.id) return
  creatingCashierBillingId.value = billing.id
  try {
    const created = await createOrGetCashierByBilling(Number(billing.id))
    const cashierNo = created?.cashierNo || ''
    if (!cashierNo) {
      ElMessage.error('收银台创建失败，请稍后重试')
      return
    }
    const targetUrl = `${window.location.origin}/cashier/${cashierNo}`
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(targetUrl)
    } else {
      // 降级方案
      const textarea = document.createElement('textarea')
      textarea.value = targetUrl
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
    }
    ElMessage.success('收银台链接已复制到剪贴板')
  } catch (error) {
    ElMessage.error(error?.message || '复制失败，请稍后重试')
  } finally {
    creatingCashierBillingId.value = null
  }
}

const handleEditTask = (task) => {
  emit('editTask', task)
}

// 编辑订单
const handleEdit = () => {
  emit('edit', props.orderDetail)
}

// 关闭驳回弹窗
const closeRejectPopover = () => {
  showRejectPopover.value = false
  rejectReason.value = ''
}

// 审批操作
const handleApprove = async (approved) => {
  if (!approved && !rejectReason.value?.trim()) {
    ElMessage.warning('请填写驳回原因')
    return
  }
  approvalSubmitting.value = true
  try {
    emit('approve', {
      orderId: props.orderDetail.id,
      approved,
      reason: approved ? '' : rejectReason.value.trim()
    })
    showApprovePopover.value = false
    closeRejectPopover()
  } finally {
    approvalSubmitting.value = false
  }
}

// 执行人相关方法
const openExecutorPopover = () => {
  showExecutorPopover.value = true
  newExecutorId.value = null
}

const closeExecutorPopover = () => {
  showExecutorPopover.value = false
  newExecutorId.value = null
}

let executorSearchTimer = null
const fetchExecutors = async (query = '', isLoadMore = false) => {
  if (executorLoading.value) return

  executorLoading.value = true
  try {
    const params = {
      page: isLoadMore ? executorPagination.page + 1 : 1,
      size: executorPagination.size,
      status: 0
    }
    if (query) {
      params.username = query
    }
    const result = await getUserList(params)
    const records = result?.records || []
    if (isLoadMore) {
      const existingIds = new Set(executorList.value.map(item => item.id))
      executorList.value = [
        ...executorList.value,
        ...records.filter(item => !existingIds.has(item.id))
      ]
      executorPagination.page = result?.current || executorPagination.page + 1
    } else {
      executorList.value = records
      executorPagination.page = result?.current || 1
    }
    executorPagination.total = result?.total || 0
  } catch (error) {
    console.error('获取执行人列表失败:', error)
  } finally {
    executorLoading.value = false
  }
}

const handleExecutorSearch = (query) => {
  executorSearchKeyword.value = query
  if (executorSearchTimer) {
    clearTimeout(executorSearchTimer)
  }
  executorSearchTimer = setTimeout(() => {
    fetchExecutors(query, false)
  }, 300)
}

const handleExecutorDropdownVisible = (visible) => {
  if (visible && executorList.value.length === 0) {
    fetchExecutors('', false)
  }
}

const loadMoreExecutors = () => {
  fetchExecutors(executorSearchKeyword.value, true)
}

const handleChangeExecutor = async () => {
  if (!newExecutorId.value) {
    ElMessage.warning('请选择新执行人')
    return
  }
  executorSubmitting.value = true
  try {
    await changeOrderExecutor({
      orderId: props.orderDetail.id,
      executorId: newExecutorId.value
    })
    ElMessage.success('执行人更换成功')
    closeExecutorPopover()
    emit('executorChanged')
  } catch (error) {
    ElMessage.error(error.message || '更换执行人失败')
  } finally {
    executorSubmitting.value = false
  }
}

const currentCustomerId = computed(() => props.orderDetail?.customer?.id || props.orderDetail?.customerId || null)

const notifyRefreshDetail = () => {
  emit('refreshDetail')
}

const reminderStatusText = (status) => {
  const map = {
    ENABLED: '已启用',
    DISABLED: '已停用',
    DELETED: '已删除'
  }
  return map[status] || '未知'
}

const reminderStatusClass = (status) => {
  const map = {
    ENABLED: 'bg-green-100 text-green-700',
    DISABLED: 'bg-slate-100 text-slate-600',
    DELETED: 'bg-red-100 text-red-700'
  }
  return map[status] || 'bg-slate-100 text-slate-600'
}

const reminderRepeatText = (reminder) => {
  if (!reminder.repeatEnabled) {
    return '不重复'
  }
  const interval = reminder.repeatIntervalMinutes || 0
  const count = reminder.maxReminderCount || 0
  return `每 ${interval} 分钟，最多 ${count} 次`
}

const resetReminderForm = () => {
  reminderForm.id = null
  reminderForm.title = ''
  reminderForm.content = ''
  reminderForm.firstSendTime = ''
  reminderForm.repeatEnabled = false
  reminderForm.repeatIntervalMinutes = 1440
  reminderForm.maxReminderCount = 3
  reminderForm.deadlineTime = ''
  reminderForm.autoCloseAtDeadline = false
  reminderForm.receiverUserIds = []
}

const openAddReminderDialog = () => {
  isEditingReminder.value = false
  resetReminderForm()
  reminderDialogVisible.value = true
}

const openEditReminderDialog = (reminder) => {
  isEditingReminder.value = true
  reminderForm.id = reminder.id
  reminderForm.title = reminder.title || ''
  reminderForm.content = reminder.content || ''
  reminderForm.firstSendTime = toDatetimeLocal(reminder.firstSendTime)
  reminderForm.repeatEnabled = !!reminder.repeatEnabled
  reminderForm.repeatIntervalMinutes = reminder.repeatIntervalMinutes || 1440
  reminderForm.maxReminderCount = reminder.maxReminderCount || 3
  reminderForm.deadlineTime = toDatetimeLocal(reminder.deadlineTime)
  reminderForm.autoCloseAtDeadline = !!reminder.autoCloseAtDeadline
  reminderForm.receiverUserIds = (reminder.receivers || []).map(item => item.userId).filter(Boolean)
  reminderDialogVisible.value = true
}

const closeReminderDialog = () => {
  reminderDialogVisible.value = false
  reminderSubmitting.value = false
}

const isReminderReceiverSelected = (userId) => reminderForm.receiverUserIds.includes(userId)

const toggleReminderReceiver = (userId) => {
  if (!userId) return
  if (isReminderReceiverSelected(userId)) {
    reminderForm.receiverUserIds = reminderForm.receiverUserIds.filter(id => id !== userId)
  } else {
    reminderForm.receiverUserIds = [...reminderForm.receiverUserIds, userId]
  }
}

const toggleStepRemindUser = (userId) => {
  if (!userId) return
  const idx = stepForm.remindUserIds.indexOf(userId)
  if (idx >= 0) {
    stepForm.remindUserIds.splice(idx, 1)
  } else {
    stepForm.remindUserIds.push(userId)
  }
}

const toggleAnnotationRemindUser = (userId) => {
  if (!userId) return
  const idx = annotationForm.remindUserIds.indexOf(userId)
  if (idx >= 0) {
    annotationForm.remindUserIds.splice(idx, 1)
  } else {
    annotationForm.remindUserIds.push(userId)
  }
}

let reminderUserSearchTimer = null
const fetchReminderUsers = async (query = '', isLoadMore = false) => {
  if (reminderUserLoading.value) return

  reminderUserLoading.value = true
  try {
    const params = {
      page: isLoadMore ? reminderUserPagination.page + 1 : 1,
      size: reminderUserPagination.size,
      status: 0
    }
    if (query) {
      params.username = query
    }
    const result = await getUserList(params)
    const records = result?.records || []
    if (isLoadMore) {
      const existingIds = new Set(reminderUserList.value.map(item => item.id))
      reminderUserList.value = [
        ...reminderUserList.value,
        ...records.filter(item => !existingIds.has(item.id))
      ]
      reminderUserPagination.page = result?.current || reminderUserPagination.page + 1
    } else {
      reminderUserList.value = records
      reminderUserPagination.page = result?.current || 1
    }
    reminderUserPagination.total = result?.total || 0
  } catch (error) {
    console.error('获取提醒接收人列表失败:', error)
  } finally {
    reminderUserLoading.value = false
  }
}

const handleReminderUserSearch = (query) => {
  reminderUserKeyword.value = query
  if (reminderUserSearchTimer) {
    clearTimeout(reminderUserSearchTimer)
  }
  reminderUserSearchTimer = setTimeout(() => {
    fetchReminderUsers(query, false)
  }, 300)
}

const handleReminderUserDropdownVisible = (visible) => {
  if (visible && reminderUserList.value.length === 0) {
    fetchReminderUsers('', false)
  }
}

const loadMoreReminderUsers = () => {
  fetchReminderUsers(reminderUserKeyword.value, true)
}

const buildReminderPayload = () => {
  const repeatEnabled = !!reminderForm.repeatEnabled
  return {
    id: reminderForm.id || undefined,
    title: reminderForm.title.trim(),
    content: reminderForm.content.trim() || undefined,
    firstSendTime: toApiDatetime(reminderForm.firstSendTime),
    repeatEnabled,
    repeatIntervalMinutes: repeatEnabled ? Number(reminderForm.repeatIntervalMinutes || 0) : 0,
    maxReminderCount: repeatEnabled ? Number(reminderForm.maxReminderCount || 0) : 0,
    deadlineTime: toApiDatetime(reminderForm.deadlineTime),
    autoCloseAtDeadline: !!reminderForm.autoCloseAtDeadline,
    receiverUserIds: Array.from(new Set(reminderForm.receiverUserIds.filter(Boolean)))
  }
}

const validateReminderForm = () => {
  if (!reminderForm.title.trim()) {
    ElMessage.warning('请输入提醒标题')
    return false
  }
  if (!reminderForm.firstSendTime) {
    ElMessage.warning('请选择首次提醒时间')
    return false
  }
  if (reminderForm.receiverUserIds.length === 0) {
    ElMessage.warning('请选择提醒接收人')
    return false
  }
  if (reminderForm.repeatEnabled) {
    if (Number(reminderForm.repeatIntervalMinutes || 0) <= 0) {
      ElMessage.warning('重复间隔必须大于0')
      return false
    }
    if (Number(reminderForm.maxReminderCount || 0) <= 0) {
      ElMessage.warning('最大提醒次数必须大于0')
      return false
    }
  }
  if (reminderForm.autoCloseAtDeadline && !reminderForm.deadlineTime) {
    ElMessage.warning('自动关闭提醒需要填写截止时间')
    return false
  }
  if (reminderForm.deadlineTime && reminderForm.deadlineTime < reminderForm.firstSendTime) {
    ElMessage.warning('截止时间不能早于首次提醒时间')
    return false
  }
  return true
}

const handleSubmitReminder = async () => {
  if (!validateReminderForm()) return
  reminderSubmitting.value = true
  try {
    const payload = buildReminderPayload()
    if (isEditingReminder.value) {
      await updateOrderReminder(props.orderDetail.id, reminderForm.id, payload)
      ElMessage.success('订单提醒已更新')
    } else {
      await createOrderReminder(props.orderDetail.id, payload)
      ElMessage.success('订单提醒已添加')
    }
    closeReminderDialog()
    notifyRefreshDetail()
  } catch (error) {
    ElMessage.error(error?.message || '保存订单提醒失败')
  } finally {
    reminderSubmitting.value = false
  }
}

const handleToggleReminder = async (reminder) => {
  try {
    if (reminder.status === 'ENABLED') {
      await disableOrderReminder(props.orderDetail.id, reminder.id)
      ElMessage.success('订单提醒已停用')
    } else {
      await enableOrderReminder(props.orderDetail.id, reminder.id)
      ElMessage.success('订单提醒已启用')
    }
    notifyRefreshDetail()
  } catch (error) {
    ElMessage.error(error?.message || '更新提醒状态失败')
  }
}

const handleDeleteReminder = async (reminder) => {
  try {
    await ElMessageBox.confirm(`确认删除提醒“${reminder.title || ''}”？`, '删除确认', {
      type: 'warning',
      confirmButtonText: '确认删除',
      cancelButtonText: '取消'
    })
  } catch {
    return
  }
  try {
    await deleteOrderReminder(props.orderDetail.id, reminder.id)
    ElMessage.success('订单提醒已删除')
    notifyRefreshDetail()
  } catch (error) {
    ElMessage.error(error?.message || '删除订单提醒失败')
  }
}

const getCandidateVisibilityText = (visibleToCustomer) => {
  return visibleToCustomer ? '对客户可见' : '对客户隐藏'
}

const openMaterialDetailDialog = (material) => {
  if (!material?.currentUserMaterialId) {
    ElMessage.warning('当前未绑定资料')
    return
  }
  selectedMaterialDetailId.value = material.currentUserMaterialId
  materialDetailDialogVisible.value = true
}

const openBindingDialog = async (material) => {
  if (!currentCustomerId.value) {
    ElMessage.warning('订单未关联客户，无法选择资料')
    return
  }
  bindingTargetMaterial.value = material
  selectedCandidateId.value = material.currentUserMaterialId || null
  candidateKeyword.value = ''
  candidateVisibilityFilter.value = ''
  materialCandidates.value = []
  bindingDialogVisible.value = true
  bindingDialogLoading.value = true
  try {
    const result = await listCustomerMaterialInstances(currentCustomerId.value, {
      definitionCode: material.definitionCode
    })
    const list = Array.isArray(result) ? result : []
    materialCandidates.value = list.map(item => ({
      ...item,
      isCurrentBinding: item.id === material.currentUserMaterialId
    }))
  } catch (error) {
    ElMessage.error(error?.message || '获取资料候选列表失败')
  } finally {
    bindingDialogLoading.value = false
  }
}

const closeBindingDialog = () => {
  bindingDialogVisible.value = false
  bindingTargetMaterial.value = null
  selectedCandidateId.value = null
  candidateKeyword.value = ''
  candidateVisibilityFilter.value = ''
  materialCandidates.value = []
}

const ensureCandidateVisibleToCustomer = async (candidate) => {
  if (candidate.visibleToCustomer !== false) {
    return
  }
  const detail = await getCustomerMaterialDetailById(candidate.id)
  await updateCustomerMaterialVisibility({
    userMaterialId: detail.id,
    customerId: detail.customerId,
    definitionCode: detail.definitionCode,
    name: detail.name,
    formData: detail.formData,
    expireDate: detail.expireDate,
    visibleToCustomer: true
  })
}

const handleConfirmBinding = async () => {
  const targetMaterial = bindingTargetMaterial.value
  const selectedCandidate = filteredMaterialCandidates.value.find(item => item.id === selectedCandidateId.value)
  if (!targetMaterial || !selectedCandidate) {
    ElMessage.warning('请选择要绑定的资料')
    return
  }
  try {
    if (selectedCandidate.visibleToCustomer === false) {
      await ElMessageBox.confirm(
        '该资料当前对客户不可见，绑定后将自动改为对客户可见，是否继续？',
        '绑定确认',
        {
          type: 'warning',
          confirmButtonText: '继续绑定',
          cancelButtonText: '取消'
        }
      )
    }
  } catch {
    return
  }

  bindingSubmitting.value = true
  try {
    await ensureCandidateVisibleToCustomer(selectedCandidate)
    await bindOrderMaterial(targetMaterial.id, {
      orderId: props.orderDetail.id,
      userMaterialId: selectedCandidate.id,
      bindSource: 'ADMIN'
    })
    ElMessage.success(targetMaterial.isBound ? '资料更换成功' : '资料绑定成功')
    closeBindingDialog()
    notifyRefreshDetail()
  } catch (error) {
    ElMessage.error(error?.message || '资料绑定失败')
  } finally {
    bindingSubmitting.value = false
  }
}

const openUnbindDialog = (material) => {
  unbindTargetMaterial.value = material
  unbindReason.value = ''
  unbindDialogVisible.value = true
}

const closeUnbindDialog = () => {
  unbindDialogVisible.value = false
  unbindReason.value = ''
  unbindTargetMaterial.value = null
}

const handleConfirmUnbind = async () => {
  if (!unbindTargetMaterial.value?.currentBindingId) {
    return
  }
  unbindSubmitting.value = true
  try {
    await unbindOrderMaterial(unbindTargetMaterial.value.currentBindingId, unbindReason.value.trim())
    ElMessage.success('资料解绑成功')
    closeUnbindDialog()
    notifyRefreshDetail()
  } catch (error) {
    ElMessage.error(error?.message || '资料解绑失败')
  } finally {
    unbindSubmitting.value = false
  }
}

const openReviewDialog = (material, approved) => {
  reviewTargetMaterial.value = material
  reviewForm.approved = approved
  reviewForm.reviewRemark = ''
  reviewDialogVisible.value = true
}

const closeReviewDialog = () => {
  reviewDialogVisible.value = false
  reviewForm.approved = true
  reviewForm.reviewRemark = ''
  reviewTargetMaterial.value = null
}

const handleSubmitReview = async () => {
  if (!reviewTargetMaterial.value?.currentBindingId) {
    return
  }
  if (!reviewForm.approved && !reviewForm.reviewRemark.trim()) {
    ElMessage.warning('请填写驳回原因')
    return
  }
  reviewSubmitting.value = true
  try {
    await reviewOrderMaterialBinding(reviewTargetMaterial.value.currentBindingId, {
      approved: reviewForm.approved,
      reviewRemark: reviewForm.reviewRemark.trim() || undefined
    })
    ElMessage.success(reviewForm.approved ? '审核通过' : '已驳回')
    closeReviewDialog()
    notifyRefreshDetail()
  } catch (error) {
    ElMessage.error(error?.message || '审核操作失败')
  } finally {
    reviewSubmitting.value = false
  }
}

// ── 服务跟踪方法 ──

// 步骤状态辅助
const stepStatusMap = {
  0: { text: '待处理', class: 'bg-slate-100 text-slate-600', dot: 'bg-slate-400', border: 'border-slate-200', headerBg: 'bg-white' },
  1: { text: '进行中', class: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500', border: 'border-blue-300', headerBg: 'bg-blue-50' },
  2: { text: '已完成', class: 'bg-green-100 text-green-700', dot: 'bg-green-500', border: 'border-green-300', headerBg: 'bg-green-50' }
}

const stepStatusText = (status) => stepStatusMap[status]?.text || '未知'
const stepStatusBadgeClass = (status) => stepStatusMap[status]?.class || 'bg-slate-100 text-slate-600'
const stepDotClass = (status) => stepStatusMap[status]?.dot || 'bg-slate-400'
const stepBorderClass = (status) => stepStatusMap[status]?.border || 'border-slate-200'
const stepHeaderBgClass = (status) => stepStatusMap[status]?.headerBg || 'bg-white'

const stepDialogTitle = computed(() => isEditingStep.value ? '编辑步骤' : '添加步骤')

// 日期格式转换：yyyy-MM-dd HH:mm:ss → yyyy-MM-ddTHH:mm（用于 datetime-local input）
const toDatetimeLocal = (datetime) => {
  if (!datetime) return ''
  return datetime.replace(' ', 'T').substring(0, 16)
}

// 日期格式转换：datetime-local → ISO 格式（发送给后端）
const toApiDatetime = (datetimeLocal) => {
  if (!datetimeLocal) return null
  return datetimeLocal + ':00'
}

// 加载跟踪数据
const loadTrackingData = async () => {
  if (!props.orderDetail?.id) return
  trackingLoading.value = true
  try {
    const data = await getOrderTracking(props.orderDetail.id)
    steps.value = data.steps || []
  } catch (error) {
    console.error('加载服务跟踪数据失败:', error)
  } finally {
    trackingLoading.value = false
  }
}

// 切换步骤展开/收起
const toggleStepExpand = (stepId) => {
  const newSet = new Set(expandedStepIds.value)
  if (newSet.has(stepId)) {
    newSet.delete(stepId)
  } else {
    newSet.add(stepId)
  }
  expandedStepIds.value = newSet
}

// 添加步骤
const openAddStepDialog = () => {
  isEditingStep.value = false
  stepForm.id = null
  stepForm.name = ''
  stepForm.content = ''
  stepForm.status = 0
  stepForm.enableReminder = false
  stepForm.nextRemindAt = ''
  stepForm.remindUserIds = []
  stepDialogVisible.value = true
}

// 编辑步骤
const openEditStepDialog = (step) => {
  isEditingStep.value = true
  stepForm.id = step.id
  stepForm.name = step.name || ''
  stepForm.content = step.content || ''
  stepForm.status = step.status
  stepForm.enableReminder = !!step.nextRemindAt
  stepForm.nextRemindAt = toDatetimeLocal(step.nextRemindAt)
  stepForm.remindUserIds = []
  stepDialogVisible.value = true
}

const closeStepDialog = () => {
  stepDialogVisible.value = false
}

// 提交步骤（新增/编辑）
const handleSubmitStep = async () => {
  if (!stepForm.name) {
    ElMessage.warning('请输入步骤名称')
    return
  }
  if (!stepForm.content) {
    ElMessage.warning('请输入步骤内容')
    return
  }
  stepSubmitting.value = true
  try {
    const remindAt = stepForm.enableReminder ? toApiDatetime(stepForm.nextRemindAt) : null
    const remindIds = stepForm.enableReminder && stepForm.remindUserIds.length > 0 ? stepForm.remindUserIds : undefined
    if (isEditingStep.value) {
      await updateServiceStep({
        id: stepForm.id,
        name: stepForm.name || undefined,
        content: stepForm.content || undefined,
        status: stepForm.status,
        nextRemindAt: remindAt,
        remindUserIds: remindIds
      })
      ElMessage.success('步骤更新成功')
    } else {
      await createServiceStep({
        orderId: props.orderDetail.id,
        name: stepForm.name,
        content: stepForm.content,
        nextRemindAt: remindAt,
        remindUserIds: remindIds
      })
      ElMessage.success('步骤添加成功')
    }
    closeStepDialog()
    await loadTrackingData()
  } catch (error) {
    ElMessage.error(error.message || '操作失败')
  } finally {
    stepSubmitting.value = false
  }
}

// 删除步骤
const handleDeleteStep = async (stepId) => {
  try {
    await ElMessageBox.confirm('确认删除该步骤？步骤下的批注和提醒也将一并删除。', '删除确认', {
      type: 'warning',
      confirmButtonText: '确认删除',
      cancelButtonText: '取消'
    })
  } catch {
    return
  }
  try {
    await deleteServiceStep(stepId)
    ElMessage.success('步骤已删除')
    await loadTrackingData()
  } catch (error) {
    ElMessage.error(error.message || '删除失败')
  }
}

// 更新步骤状态
const handleStepStatusChange = async (stepId, status) => {
  try {
    await updateStepStatus({ id: stepId, status })
    ElMessage.success('状态已更新')
    await loadTrackingData()
  } catch (error) {
    ElMessage.error(error.message || '状态更新失败')
  }
}

// 添加步骤批注
const openAddStepAnnotationDialog = (stepId) => {
  annotationTargetStepId.value = stepId
  annotationForm.content = ''
  annotationForm.enableReminder = false
  annotationForm.nextRemindAt = ''
  annotationForm.remindUserIds = []
  annotationDialogVisible.value = true
}

const closeAnnotationDialog = () => {
  annotationDialogVisible.value = false
}

// 提交批注
const handleSubmitAnnotation = async () => {
  if (!annotationForm.content) {
    ElMessage.warning('请输入批注内容')
    return
  }
  annotationSubmitting.value = true
  try {
    const bizType = annotationTargetStepId.value ? 'ORDER_STEP' : 'ORDER'
    const bizId = annotationTargetStepId.value || props.orderDetail.id
    await createOrderAnnotation({
      bizType,
      bizId,
      content: annotationForm.content,
      nextRemindAt: annotationForm.enableReminder ? toApiDatetime(annotationForm.nextRemindAt) : null,
      remindUserIds: annotationForm.remindUserIds.length > 0 ? annotationForm.remindUserIds : undefined
    })
    ElMessage.success('批注已添加')
    closeAnnotationDialog()
    await loadTrackingData()
  } catch (error) {
    ElMessage.error(error.message || '添加批注失败')
  } finally {
    annotationSubmitting.value = false
  }
}

// 监听订单详情变化，加载跟踪数据
watch(() => props.orderDetail?.id, (newId) => {
  if (newId) {
    loadTrackingData()
  }
}, { immediate: true })

const statusMap = {
  0: { text: '待开始', class: 'bg-slate-100 text-slate-600' },
  1: { text: '服务中', class: 'bg-blue-100 text-blue-700' },
  2: { text: '已完成', class: 'bg-green-100 text-green-700' },
  3: { text: '已取消', class: 'bg-red-100 text-red-700' }
}

const billingStatusMap = {
  0: { text: '待收', class: 'bg-amber-100 text-amber-700' },
  1: { text: '已收', class: 'bg-green-100 text-green-700' },
  2: { text: '部分收', class: 'bg-blue-100 text-blue-700' },
  3: { text: '已核销', class: 'bg-purple-100 text-purple-700' }
}

const taskStatusMap = {
  0: { text: '待开始', class: 'bg-slate-100 text-slate-600' },
  1: { text: '进行中', class: 'bg-blue-100 text-blue-700' },
  2: { text: '已完成', class: 'bg-green-100 text-green-700' },
  3: { text: '已暂停', class: 'bg-amber-100 text-amber-700' }
}

const getStatusClass = (status) => statusMap[status]?.class || 'bg-slate-100 text-slate-600'
const getStatusText = (status) => statusMap[status]?.text || '未知'
const hasPendingApproval = (order) => order?.pendingApprovalInstanceId != null
const getBillingStatusClass = (status) => billingStatusMap[status]?.class || 'bg-slate-100 text-slate-600'
const getBillingStatusText = (status) => billingStatusMap[status]?.text || '未知'
const getTaskStatusClass = (status) => taskStatusMap[status]?.class || 'bg-slate-100 text-slate-600'
const getTaskStatusText = (status) => taskStatusMap[status]?.text || '未知'

const getProgressBarClass = (status) => {
  switch (status) {
    case 0: return 'bg-slate-400'
    case 1: return 'bg-gradient-to-r from-blue-400 to-blue-600'
    case 2: return 'bg-gradient-to-r from-green-400 to-green-600'
    case 3: return 'bg-amber-400'
    default: return 'bg-slate-400'
  }
}
</script>
